import React, { useEffect, useState } from "react";
import { Box } from "src/components/Box";
import { Input } from "src/components/Input";
import { TextArea } from "src/components/TextArea";
import { Dropdown } from "src/components/Dropdown";
import {
  DatePicker,
  FilePickerErrorType,
  FilePickerWithPreview,
  ImageFilePickerWithCamera,
} from "src/components";
import { Text } from "src/components/Text";

import { Button } from "src/components/Button";
import { useTranslation } from "src/hooks/useTranslation";
import { OwnerInfoModal } from "./OwnerInfo.modal";
import { KtpScanGuideModal } from "./KtpScanGuide.modal";
import { KtpScanErrorModal } from "./KtpScanError.modal";
import { Link, useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { KtpScanError, useOwnerInfoState } from "./OwnerInfo.state";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { LoadingOverlay } from "src/components/LoadingOverlay";
import { usePassportStore } from "src/stores/Passport.store";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { CookieNames, setCookie } from "src/helpers/cookies";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";
import { Logger } from "src/helpers/logger";
import { errFormatter } from "src/helpers/errorFormatter";
import { Info } from "@asphalt-react/iconpack";
import { AlertBox } from "src/components/AlertBox";
import { Breakpoint } from "src/components/Breakpoint";
import {
  BoxButtonCtaEl,
  BoxColumnEl,
  BoxColumnSectionEl,
  BoxDropdownEl,
  BoxInputEl,
  BoxRowSectionEl,
} from "./OwnerInfo.styled";
import { format } from "date-fns";
import { Citizenship } from "src/constants";
import { Liveness } from "./liveness";
import { GuideModal } from "./OwnerInfo.guide";

const { National, International } = Citizenship;
const KTP_SCAN_FILE_TYPES = ["image/jpeg", "image/jpg"];
const KTP_GENDERS = ["MALE", "FEMALE"];
const {
  NetworkError,
  FileInvalidType,
  FileMaxSize,
  FileMaxCount,
} = FilePickerErrorType;

const useImagePickerTranslation = () => {
  const translate = useTranslation();

  return {
    getErrorTranslation: (error: string) => {
      switch (error) {
        case NetworkError:
          return translate("owner_info.selfie.errors.general");
        case FileInvalidType:
          return translate("owner_info.selfie.errors.image_invalid_type");
        case FileMaxCount:
          return translate("owner_info.selfie.errors.too_many_files");
        case FileMaxSize:
          return translate("owner_info.selfie.errors.image_too_large");
        default:
          return translate("owner_info.selfie.errors.general");
      }
    },
    guideProps: {
      content: <GuideModal />,
      actionText: translate("owner_info.selfie.guide.cta"),
    },
    contentProps: {
      helperText: translate("owner_info.selfie.helper_text"),
      takePicText: translate("owner_info.selfie.cta.take_photo"),
      uploadText: translate("owner_info.selfie.cta.upload"),
      reUploadText: translate("owner_info.selfie.cta.re_upload"),
      reTakePicText: translate("owner_info.selfie.cta.retake_photo"),
    },
  };
};

export const OwnerInfoForm = observer(() => {
  const translate = useTranslation();
  const passportStore = usePassportStore();
  const ownerInfoState = useOwnerInfoState();
  const {
    form,
    additionalForm,
    errorFields,
    citizenshipOptions,
    selectedCitizenship,
    isFieldNotValid,
    placeOfBirthOptions,
    applyPlaceOfBirthSearch,
    nationalityOptions,
    applyNationalitySearch,
  } = ownerInfoState;
  const {
    selectedBusinessType,
    selectedPaymentMethod,
    outlet,
    activation,
    authUser,
  } = passportStore!;
  const [showModalKTP, setShowModalKTP] = useState(false);
  const [ktpScanError, setKtpScanError] = useState<KtpScanError>();
  const history = useHistory();

  const errorModalStore = useErrorModalStore();
  const imagePickerTranslation = useImagePickerTranslation();

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_owner_opened" });
  }, []);

  useEffect(() => {
    ownerInfoState.fillForm(outlet, authUser);
    ownerInfoState.fillAdditionalForm(activation);
  }, [outlet, ownerInfoState, authUser, activation]);

  useEffect(() => {
    ownerInfoState.onKtpScanError = setKtpScanError;

    return () => {
      ownerInfoState.onKtpScanError = undefined;
    };
  }, [ownerInfoState]);

  useEffect(() => {
    ownerInfoState.loadKtpScan();

    return () => ownerInfoState.cancelKtpScanPolling();
  }, [ownerInfoState]);

  const _handleOnSubmit = async (e: any) => {
    try {
      e?.preventDefault();
      if (isFieldNotValid) {
        ownerInfoState.validateRequiredFields();
        return;
      }
      await Promise.all([
        passportStore?.updateOutlet(ownerInfoState.validForm),
        passportStore?.updateActivation(ownerInfoState.validAdditionalForm),
      ]);
      eventDispatcher.publish({ action: "ob_owner_next" });
      setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.businessInfo);
      history.push(PASSPORT_ROUTES.businessInfo);
    } catch (err) {
      Logger.error(err as Error);
      const { status, message } = errFormatter(err);

      eventDispatcher.publish({
        action: "ob_owner_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      errorModalStore.showAlert();
      errorModalStore.retryCta = _handleOnSubmit;
    }
  };

  const _handleOnUpload = async (file: File, name: string) => {
    try {
      if (name.toLowerCase().includes("selfie")) {
        ownerInfoState.errorFields.selfie_upload.error = false;
        await ownerInfoState.uploadFileSelfie(
          passportStore?.authUser.id!,
          file
        );
        eventDispatcher.publish({ action: "ob_upload_selfie_success" });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_selfie_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.selfie_upload.error = true;
    }

    try {
      if (name.toLowerCase().includes("ktp")) {
        ownerInfoState.errorFields.ktp_upload.error = false;
        await ownerInfoState.uploadFileKtp(passportStore?.authUser?.id!, file);
        eventDispatcher.publish({ action: "ob_upload_ktp_success" });
        if (ownerInfoState.isKtpScanActive) {
          ownerInfoState.startKtpScan(file.type);
        }
      }
    } catch (error) {
      ownerInfoState.errorFields.ktp_upload.error = true;
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_ktp_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
    }

    try {
      if (name.toLowerCase().includes("kitas")) {
        ownerInfoState.errorFields.kitas_upload.error = false;
        await ownerInfoState.uploadFileKitas(
          passportStore?.authUser?.id!,
          file
        );
        eventDispatcher.publish({
          action: "ob_upload_kitas_success",
          payload: {
            fileType: file.type,
          },
        });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_kitas_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.kitas_upload.error = true;
    }

    try {
      if (name.toLowerCase().includes("passport")) {
        ownerInfoState.errorFields.passport_upload.error = false;
        await ownerInfoState.uploadFilePassport(
          passportStore?.authUser?.id!,
          file
        );
        eventDispatcher.publish({
          action: "ob_upload_passport_success",
        });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_passport_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.passport_upload.error = true;
    }

    try {
      if (name.toLowerCase().includes("npwp")) {
        ownerInfoState.errorFields.npwp_upload.error = false;
        await ownerInfoState.uploadFileNpwp(passportStore?.authUser.id!, file);
        eventDispatcher.publish({ action: "ob_upload_npwp_success" });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_npwp_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.npwp_upload.error = true;
    }

    ownerInfoState.applyFileUpload(file, name);
  };

  return (
    <>
      <Breakpoint medium up>
        <Text marginBottomXl fontSize="xlarge" fontWeight="demi">
          {translate("owner_info.title")}
        </Text>
      </Breakpoint>
      <Box as="form" onSubmit={_handleOnSubmit}>
        <Box marginBottomXl>
          <AlertBox color="info" icon={<Info />}>
            {translate(
              `owner_info.${selectedBusinessType}.${selectedCitizenship.toLowerCase()}.alert`
            )}
          </AlertBox>
        </Box>
        <BoxColumnEl>
          <BoxDropdownEl marginBottomL>
            <Dropdown
              items={citizenshipOptions}
              label={translate(
                `owner_info.${selectedBusinessType}.citizenship.label`
              )}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.citizenship.placeholder`
              )}
              onChange={({ id }) => {
                ownerInfoState.applySelectedCitizenship(id as Citizenship);
              }}
              size="m"
            />
          </BoxDropdownEl>
          {ownerInfoState.isKtpOcrEnabled && selectedCitizenship === National && (
            <Input
              label={translate(
                `owner_info.${selectedBusinessType}.email.label`
              )}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.email.placeholder`
              )}
              onChange={(e) =>
                ownerInfoState.handleAdditionalOnFieldChange(
                  "director_email",
                  e.target.value
                )
              }
              onBlur={() => {
                ownerInfoState.validateAdditionalForm("director_email");
              }}
              error={errorFields.director_email.error}
              helperText={errorFields.director_email.helperText}
              value={additionalForm.director_email}
              type="email"
              maxLength={255}
            />
          )}
        </BoxColumnEl>

        {selectedCitizenship === National && (
          <BoxRowSectionEl>
            <Text marginBottomS fontWeight="demi" fontSize="normal">
              {translate(
                `owner_info.${selectedBusinessType}.upload_identity_card.label`
              )}
            </Text>
            <BoxColumnEl style={{ alignItems: "stretch" }}>
              <Box marginBottomM>
                <FilePickerWithPreview
                  key={ownerInfoState.ktpUploadKey}
                  onInfoClick={() => {
                    setShowModalKTP(true);
                    eventDispatcher.publish({
                      action: "ob_upload_ktp_clicked",
                    });
                  }}
                  onClick={() =>
                    eventDispatcher.publish({ action: "ob_upload_ktp" })
                  }
                  maxSize={10}
                  onChange={(file) => _handleOnUpload(file, "ktp")}
                  previewUrl={ownerInfoState.fileUpload?.ktp.url}
                  mediaType={ownerInfoState.fileUpload?.ktp.mediaType}
                  metadata={ownerInfoState.fileUpload?.ktp.metadata}
                  isLoading={
                    ownerInfoState?.loadingState.has.uploadFileKtp ||
                    ownerInfoState.isKtpScanning
                  }
                  tempFile={ownerInfoState.fileUpload.ktp.temp}
                  onDelete={() => {
                    ownerInfoState.deleteFileUpload("ktp");
                    ownerInfoState.errorFields.ktp_upload.error = undefined;
                    ownerInfoState.clearKtpScan();
                  }}
                  onError={(error) => {
                    eventDispatcher.publish({
                      action: "ob_upload_ktp_error",
                      payload: { message: error.message },
                    });
                    if (ownerInfoState.isKtpScanActive) {
                      ownerInfoState.onKtpScanError?.("INVALID_FORMAT");
                    }
                  }}
                  isError={ownerInfoState.errorFields.ktp_upload.error}
                  buttonText="Upload KTP"
                  {...(ownerInfoState.isKtpScanActive && {
                    acceptedTypes: KTP_SCAN_FILE_TYPES,
                    helperText: translate("owner_info.ktp_scan.helper_text"),
                    getErrorTranslation: (error: string) =>
                      translate(
                        error === FileMaxCount
                          ? "owner_info.ktp_scan.too_many_files"
                          : error === NetworkError
                          ? "owner_info.ktp_scan.errors.TECHNICAL_ERROR.message"
                          : "owner_info.ktp_scan.errors.INVALID_FORMAT.message"
                      ),
                  })}
                />
              </Box>
              <Box marginBottomM>
                {ownerInfoState.isIndividual && (
                  <LivenessView
                    verified={ownerInfoState.additionalForm.liveness_verified!!}
                    onVerified={() => ownerInfoState.handleLivenessVerified()}
                  />
                )}
                {ownerInfoState.isCorporate && (
                  <ImageFilePickerWithCamera
                    onClick={() =>
                      eventDispatcher.publish({ action: "ob_upload_selfie" })
                    }
                    maxSize={5}
                    onChange={(file) => _handleOnUpload(file, "selfie")}
                    previewUrl={ownerInfoState.fileUpload.selfie.url}
                    isLoading={
                      ownerInfoState?.loadingState.has.uploadFileSelfie
                    }
                    tempFile={ownerInfoState.fileUpload.selfie.temp}
                    onDelete={() => {
                      ownerInfoState.deleteFileUpload("selfie");
                      ownerInfoState.errorFields.selfie_upload.error = undefined;
                    }}
                    onError={(error) => {
                      eventDispatcher.publish({
                        action: "ob_upload_selfie_error",
                        payload: { message: error.message },
                      });
                    }}
                    isError={ownerInfoState.errorFields.selfie_upload.error}
                    {...imagePickerTranslation}
                  />
                )}
              </Box>
            </BoxColumnEl>
            {!ownerInfoState.isKtpFieldsHidden && (
              <>
                <BoxColumnEl>
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.identity_number.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.identity_number.placeholder`
                    )}
                    onChange={({ target }) => {
                      let { value } = target;
                      ownerInfoState.handleOnFieldChange("id_number", value);
                    }}
                    onBlur={() => {
                      ownerInfoState.validateForm("id_number", "id_number");
                    }}
                    error={errorFields.id_number.error}
                    helperText={errorFields.id_number.helperText}
                    value={form.id_number}
                    disabled={ownerInfoState.isKtpFieldLocked("id_number")}
                    pattern="^[0-9]*$"
                    maxLength={16}
                    inputMode="numeric"
                  />
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.name_on_identity_card.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.name_on_identity_card.placeholder`
                    )}
                    onChange={(e) =>
                      ownerInfoState.handleOnFieldChange(
                        "id_name",
                        e.target.value
                      )
                    }
                    onBlur={() => {
                      ownerInfoState.validateForm("id_name");
                    }}
                    error={errorFields.id_name.error}
                    helperText={errorFields.id_name.helperText}
                    value={form.id_name}
                    disabled={ownerInfoState.isKtpFieldLocked("id_name")}
                    maxLength={50}
                    pattern="^[a-z A-Z .]*$"
                  />
                </BoxColumnEl>
                <BoxColumnEl>
                  <BoxInputEl marginBottomL>
                    <Dropdown
                      typeahead
                      name="pob"
                      items={placeOfBirthOptions.map(({ id, key }) => ({
                        id,
                        key,
                        selected: id === additionalForm.pob,
                      }))}
                      onChange={(option: any) => {
                        ownerInfoState.handleAdditionalOnFieldChange(
                          "pob",
                          option?.id
                        );
                        ownerInfoState.validateAdditionalForm("pob");
                      }}
                      onInput={(event) => {
                        applyPlaceOfBirthSearch(event.currentTarget.value);
                      }}
                      label={translate(
                        "owner_info.citizenship.national.pob.label"
                      )}
                      placeholder={translate(
                        "owner_info.citizenship.national.pob.placeholder"
                      )}
                      error={!!errorFields.pob.error}
                      helperText={errorFields.pob.helperText}
                      size="m"
                      filter={false}
                      disabled={ownerInfoState.isKtpFieldLocked("pob")}
                    />
                  </BoxInputEl>
                  <BoxInputEl marginBottomL>
                    <DatePicker
                      value={
                        additionalForm.dob ? new Date(additionalForm.dob) : null
                      }
                      onDateChange={({ date }) => {
                        ownerInfoState.handleAdditionalOnFieldChange(
                          "dob",
                          date ? format(date, "yyyy-MM-dd") : null
                        );
                        ownerInfoState.validateAdditionalForm("dob");
                      }}
                      onError={() => ownerInfoState.handleInvalidDateInput()}
                      dob
                      stretch
                      disabled={ownerInfoState.isKtpFieldLocked("dob")}
                      label={translate(
                        "owner_info.citizenship.national.dob.label"
                      )}
                      placeholder={translate(
                        "owner_info.citizenship.national.dob.placeholder"
                      )}
                      error={!!errorFields.dob.error}
                      helperText={errorFields.dob.helperText}
                    />
                  </BoxInputEl>
                </BoxColumnEl>
                {ownerInfoState.isKtpScanActive && (
                  <BoxColumnEl>
                    <BoxInputEl marginBottomL>
                      <Dropdown
                        name="gender"
                        items={KTP_GENDERS.map((gender) => ({
                          id: gender,
                          key: translate(
                            `owner_info.citizenship.national.gender.options.${gender}`
                          ),
                          selected: gender === additionalForm.gender,
                        }))}
                        onChange={(option: any) => {
                          ownerInfoState.handleAdditionalOnFieldChange(
                            "gender",
                            option?.id
                          );
                          ownerInfoState.validateAdditionalForm("gender");
                        }}
                        label={translate(
                          "owner_info.citizenship.national.gender.label"
                        )}
                        placeholder={translate(
                          "owner_info.citizenship.national.gender.placeholder"
                        )}
                        error={!!errorFields.gender.error}
                        helperText={errorFields.gender.helperText}
                        size="m"
                        disabled={ownerInfoState.isKtpFieldLocked("gender")}
                      />
                    </BoxInputEl>
                    <Input
                      label={translate(
                        "owner_info.citizenship.national.occupation.label"
                      )}
                      placeholder={translate(
                        "owner_info.citizenship.national.occupation.placeholder"
                      )}
                      onChange={(e) =>
                        ownerInfoState.handleAdditionalOnFieldChange(
                          "occupation",
                          e.target.value
                        )
                      }
                      onBlur={() => {
                        ownerInfoState.validateAdditionalForm("occupation");
                      }}
                      error={errorFields.occupation.error}
                      helperText={errorFields.occupation.helperText}
                      value={additionalForm.occupation}
                      disabled={ownerInfoState.isKtpFieldLocked("occupation")}
                      maxLength={100}
                    />
                  </BoxColumnEl>
                )}
                {ownerInfoState.isPobOthers && (
                  <BoxColumnEl>
                    <Input
                      label={translate(
                        "owner_info.citizenship.national.pob_others.label"
                      )}
                      placeholder={translate(
                        "owner_info.citizenship.national.pob_others.placeholder"
                      )}
                      onChange={(e) =>
                        ownerInfoState.handleAdditionalOnFieldChange(
                          "pob_others",
                          e.target.value
                        )
                      }
                      onBlur={() => {
                        ownerInfoState.validateAdditionalForm("pob_others");
                      }}
                      error={errorFields.pob_others.error}
                      helperText={errorFields.pob_others.helperText}
                      value={additionalForm.pob_others}
                      disabled={ownerInfoState.isKtpFieldLocked("pob")}
                    />
                  </BoxColumnEl>
                )}
              </>
            )}
          </BoxRowSectionEl>
        )}

        {selectedCitizenship === International && (
          <BoxRowSectionEl>
            <Text marginBottomS fontWeight="demi" fontSize="normal">
              {translate(
                `owner_info.${selectedBusinessType}.upload_kitas.label`
              )}
            </Text>
            <BoxColumnEl style={{ alignItems: "stretch" }}>
              <Box marginBottomM>
                <FilePickerWithPreview
                  maxSize={10}
                  onChange={(e) => _handleOnUpload(e, "kitas")}
                  previewUrl={ownerInfoState.fileUpload.kitas.url}
                  mediaType={ownerInfoState.fileUpload.kitas.mediaType}
                  metadata={ownerInfoState.fileUpload.kitas.metadata}
                  isLoading={ownerInfoState?.loadingState.has.uploadFileKitas}
                  tempFile={ownerInfoState.fileUpload.kitas.temp}
                  onClick={() =>
                    eventDispatcher.publish({ action: "ob_upload_kitas" })
                  }
                  onDelete={() => {
                    ownerInfoState.deleteFileUpload("kitas");
                    ownerInfoState.errorFields.kitas_upload.error = undefined;
                  }}
                  onError={(error) =>
                    eventDispatcher.publish({
                      action: "ob_upload_kitas_error",
                      payload: {
                        message: error.message,
                      },
                    })
                  }
                  isError={ownerInfoState.errorFields.kitas_upload.error}
                />
              </Box>
              <Box marginBottomM>
                {ownerInfoState.isIndividual && (
                  <LivenessView
                    verified={ownerInfoState.additionalForm.liveness_verified!!}
                    onVerified={() => ownerInfoState.handleLivenessVerified()}
                  />
                )}
                {ownerInfoState.isCorporate && (
                  <ImageFilePickerWithCamera
                    onClick={() =>
                      eventDispatcher.publish({ action: "ob_upload_selfie" })
                    }
                    maxSize={5}
                    onChange={(file) => _handleOnUpload(file, "selfie")}
                    previewUrl={ownerInfoState.fileUpload.selfie.url}
                    isLoading={
                      ownerInfoState?.loadingState.has.uploadFileSelfie
                    }
                    tempFile={ownerInfoState.fileUpload.selfie.temp}
                    onDelete={() => {
                      ownerInfoState.deleteFileUpload("selfie");
                      ownerInfoState.errorFields.selfie_upload.error = undefined;
                    }}
                    onError={(error) => {
                      eventDispatcher.publish({
                        action: "ob_upload_selfie_error",
                        payload: { message: error.message },
                      });
                    }}
                    isError={ownerInfoState.errorFields.selfie_upload.error}
                    {...imagePickerTranslation}
                  />
                )}
              </Box>
            </BoxColumnEl>
            <BoxColumnEl>
              <Input
                label={translate(
                  `owner_info.${selectedBusinessType}.name_on_kitas.label`
                )}
                placeholder={translate(
                  `owner_info.${selectedBusinessType}.name_on_kitas.placeholder`
                )}
                onChange={(e) =>
                  ownerInfoState.handleOnFieldChange("id_name", e.target.value)
                }
                onBlur={() => {
                  ownerInfoState.validateForm("id_name");
                }}
                error={errorFields.id_name.error}
                helperText={errorFields.id_name.helperText}
                value={form.id_name}
                maxLength={50}
                pattern="^[a-z A-Z .]*$"
              />
              <BoxInputEl marginBottomL>
                <DatePicker
                  value={
                    additionalForm.dob ? new Date(additionalForm.dob) : null
                  }
                  onDateChange={({ date }) => {
                    ownerInfoState.handleAdditionalOnFieldChange(
                      "dob",
                      date ? format(date, "yyyy-MM-dd") : null
                    );
                    ownerInfoState.validateAdditionalForm("dob");
                  }}
                  onError={() => ownerInfoState.handleInvalidDateInput()}
                  dob
                  stretch
                  label={translate(
                    "owner_info.citizenship.international.dob.label"
                  )}
                  placeholder={translate(
                    "owner_info.citizenship.international.dob.placeholder"
                  )}
                  error={!!errorFields.dob.error}
                  helperText={errorFields.dob.helperText}
                />
              </BoxInputEl>
            </BoxColumnEl>
            <BoxColumnEl>
              <Input
                label={translate(
                  "owner_info.citizenship.international.pob_others.label"
                )}
                placeholder={translate(
                  "owner_info.citizenship.international.pob_others.placeholder"
                )}
                onChange={(e) =>
                  ownerInfoState.handleAdditionalOnFieldChange(
                    "pob_others",
                    e.target.value
                  )
                }
                onBlur={() => {
                  ownerInfoState.validateAdditionalForm("pob_others");
                }}
                error={errorFields.pob_others.error}
                helperText={errorFields.pob_others.helperText}
                value={additionalForm.pob_others}
              />
              {ownerInfoState.showOwnerNationality && (
                <BoxInputEl marginBottomL>
                  <Dropdown
                    typeahead
                    name="owner_nationality"
                    items={nationalityOptions.map(({ id, key }) => ({
                      id,
                      key,
                      selected: id === additionalForm.owner_nationality,
                    }))}
                    onChange={(option: any) => {
                      ownerInfoState.handleAdditionalOnFieldChange(
                        "owner_nationality",
                        option?.id
                      );
                      ownerInfoState.validateAdditionalForm(
                        "owner_nationality"
                      );
                    }}
                    onInput={(event) => {
                      applyNationalitySearch(event.currentTarget.value);
                    }}
                    label={translate(
                      "owner_info.citizenship.nationality.label"
                    )}
                    placeholder={translate(
                      "owner_info.citizenship.nationality.placeholder"
                    )}
                    error={!!errorFields.owner_nationality.error}
                    helperText={errorFields.owner_nationality.helperText}
                    size="m"
                    filter={false}
                  />
                </BoxInputEl>
              )}
            </BoxColumnEl>
          </BoxRowSectionEl>
        )}

        {selectedCitizenship === "WNI" &&
          selectedPaymentMethod === "card" &&
          selectedBusinessType === "individual" && (
            <BoxColumnSectionEl>
              <Box marginBottomL>
                <Text marginBottomS fontWeight="demi" fontSize="normal">
                  {translate(
                    `owner_info.${selectedBusinessType}.upload_npwp.label`
                  )}
                </Text>
                <Text marginBottomS fontSize="medium">
                  {translate(
                    `owner_info.${selectedBusinessType}.upload_npwp.sublabel`
                  )}
                </Text>
                <FilePickerWithPreview
                  maxSize={10}
                  onChange={(e) => _handleOnUpload(e, "npwp")}
                  previewUrl={ownerInfoState.fileUpload.npwp.url}
                  mediaType={ownerInfoState.fileUpload.npwp.mediaType}
                  metadata={ownerInfoState.fileUpload.npwp.metadata}
                  isLoading={ownerInfoState?.loadingState.has.uploadFileNpwp}
                  tempFile={ownerInfoState.fileUpload.npwp.temp}
                  onClick={() =>
                    eventDispatcher.publish({ action: "ob_upload_npwp" })
                  }
                  onDelete={() => {
                    ownerInfoState.deleteFileUpload("npwp");
                    ownerInfoState.errorFields.npwp_upload.error = undefined;
                  }}
                  onError={(error) =>
                    eventDispatcher.publish({
                      action: "ob_upload_npwp_error",
                      payload: {
                        message: error.message,
                      },
                    })
                  }
                  isError={ownerInfoState.errorFields.npwp_upload.error}
                />
              </Box>

              <Box>
                <Box>
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.name_on_npwp.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.name_on_npwp.placeholder`
                    )}
                    onChange={(e) =>
                      ownerInfoState.handleOnFieldChange(
                        "npwp_name",
                        e.target.value
                      )
                    }
                    onBlur={() => {
                      ownerInfoState.validateForm("npwp_name");
                    }}
                    error={errorFields.npwp_name.error}
                    helperText={errorFields.npwp_name.helperText}
                    defaultValue={outlet.npwpName}
                    maxLength={50}
                  />
                </Box>
                <Box>
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.npwp_number.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.npwp_number.placeholder`
                    )}
                    onChange={({ target }) => {
                      let { value } = target;
                      ownerInfoState.handleOnFieldChange("npwp", value);
                    }}
                    onBlur={() => ownerInfoState.validateForm("npwp", "npwp")}
                    error={errorFields.npwp.error}
                    helperText={errorFields.npwp.helperText}
                    value={form.npwp}
                    pattern="^[0-9]*$"
                    maxLength={16}
                    inputMode="numeric"
                  />
                </Box>
                <Box marginBottomL>
                  <TextArea
                    disabledResize
                    label={translate(
                      `owner_info.${selectedBusinessType}.address_on_npwp.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.address_on_npwp.placeholder`
                    )}
                    onChange={(e) =>
                      ownerInfoState.handleOnFieldChange(
                        "headquarters_address",
                        e.target.value
                      )
                    }
                    onBlur={() =>
                      ownerInfoState.validateForm("headquarters_address")
                    }
                    error={errorFields.headquarters_address.error}
                    helperText={errorFields.headquarters_address.helperText}
                    defaultValue={outlet.headquartersAddress}
                    maxLength={255}
                  />
                </Box>
              </Box>
            </BoxColumnSectionEl>
          )}

        {selectedCitizenship === "WNA" && (
          <BoxColumnSectionEl>
            <Box marginBottomL>
              <Text marginBottomS fontWeight="demi" fontSize="normal">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_passport.label`
                )}
              </Text>
              <Text marginBottomS fontSize="medium">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_passport.sublabel`
                )}
              </Text>
              <FilePickerWithPreview
                maxSize={10}
                onChange={(e) => _handleOnUpload(e, "passport")}
                previewUrl={ownerInfoState.fileUpload.passport.url}
                mediaType={ownerInfoState.fileUpload.passport.mediaType}
                metadata={ownerInfoState.fileUpload.passport.metadata}
                isLoading={ownerInfoState.loadingState.has.uploadFilePassport}
                tempFile={ownerInfoState.fileUpload.passport.temp}
                onClick={() =>
                  eventDispatcher.publish({ action: "ob_upload_passport" })
                }
                onDelete={() => {
                  ownerInfoState.deleteFileUpload("passport");
                  ownerInfoState.errorFields.passport_upload.error = undefined;
                }}
                onError={(error) =>
                  eventDispatcher.publish({
                    action: "ob_upload_passport_error",
                    payload: {
                      message: error.message,
                    },
                  })
                }
                isError={ownerInfoState.errorFields.passport_upload.error}
              />
            </Box>
          </BoxColumnSectionEl>
        )}

        <BoxButtonCtaEl flex justifyBetween marginTopXxl>
          <Link to={PASSPORT_ROUTES.paymentMethods}>
            <Button
              outline
              style={{ minWidth: "144px" }}
              onClick={() => {
                ownerInfoState.resetSelectedCitizenship();
                eventDispatcher.publish({ action: "ob_owner_back" });
              }}
            >
              {translate("owner_info.footer.cta_back")}
            </Button>
          </Link>
          <Button
            disabled={isFieldNotValid}
            loading={
              passportStore?.loadingState?.has?.updateOutlet! ||
              passportStore?.loadingState?.has?.updateActivation!
            }
            type="submit"
            style={{ minWidth: "144px" }}
          >
            {translate("owner_info.footer.cta_continue")}
          </Button>
        </BoxButtonCtaEl>
      </Box>

      <LoadingOverlay
        show={
          passportStore?.loadingState?.has?.updateOutlet! ||
          passportStore?.loadingState?.has?.updateActivation!
        }
      />
      <KtpScanErrorModal
        error={ktpScanError}
        onClose={() => setKtpScanError(undefined)}
      />
      {ownerInfoState.isKtpScanActive ? (
        <KtpScanGuideModal
          show={showModalKTP}
          onClose={() => setShowModalKTP(false)}
        />
      ) : (
        <OwnerInfoModal
          show={showModalKTP}
          onClose={() => setShowModalKTP(false)}
          selectedBusinessType={selectedBusinessType}
        />
      )}
    </>
  );
});

type LivenessViewProps = {
  verified: boolean;
  onVerified: () => void;
};

const LivenessView = observer(({ verified, onVerified }: LivenessViewProps) => {
  return <Liveness verified={verified} onVerified={onVerified} />;
});
